<template>
  <v-card class="class-overview" rounded="xl" elevation="2" :style="themeStyle">
    <div v-if="!hideHero" class="class-overview__hero" :style="heroStyle">
      <div class="class-overview__hero-overlay"></div>

      <div class="class-overview__hero-content">
        <div class="d-flex align-center flex-wrap">
          <v-avatar :color="theme.primary" size="54" class="mr-4 mb-2 mb-sm-0">
            <v-icon dark>{{ theme.heroIcon }}</v-icon>
          </v-avatar>

          <div class="mr-4">
            <div
              class="caption text-uppercase white--text class-overview__eyebrow"
            >
              Visualização da turma
            </div>
            <div class="text-h5 font-weight-bold white--text">
              Turma {{ classData.cod || "-" }}
            </div>
            <div class="caption white--text text--lighten-2">
              Criada em {{ classData.createdAt || "-" }}
            </div>
          </div>
        </div>

        <v-chip small dark :color="statusColor(classData.status)">
          {{ displayStatus(classData.status) }}
        </v-chip>
      </div>
    </div>

    <v-card-text class="pa-6">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="info-box">
            <div class="info-box__label-row">
              <div class="info-box__label">Nível</div>
              <v-btn
                v-if="allowLevelEdit"
                icon
                x-small
                :color="theme.primary"
                @click="openLevelDialog"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
            <div class="info-box__value">
              {{ displayLevel(classData.level) }}
            </div>
            <div v-if="allowLevelEdit" class="info-box__helper">
              Somente o professor pode atualizar o nível desta turma.
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="3">
          <div class="info-box">
            <div class="info-box__label">Formato</div>
            <div class="info-box__value">
              {{ displayFormat(classData.format) }}
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="3">
          <div class="info-box">
            <div class="info-box__label">Frequência</div>
            <div class="info-box__value">
              {{ displayFrequency(classData.frequency) }}
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="3">
          <div class="info-box">
            <div class="info-box__label">Código</div>
            <div class="info-box__value">
              {{ classData.cod || "-" }}
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <v-col cols="12" md="6">
          <div class="section-card fill-height">
            <div class="section-card__title">
              <v-icon small class="mr-2" :color="theme.primary">
                mdi-calendar-month-outline
              </v-icon>
              Cronograma da turma
            </div>

            <v-chip-group column>
              <v-chip
                v-for="(time, index) in normalizedTimes"
                :key="`${time.weekDay}-${time.start}-${index}`"
                outlined
                :color="theme.primary"
                class="mb-2"
              >
                {{ weekDayLabel(time.weekDay) }} - {{ time.start }} às
                {{ time.end }}
              </v-chip>
            </v-chip-group>

            <v-alert
              v-if="!normalizedTimes.length"
              type="info"
              text
              class="mb-0 mt-2"
            >
              Nenhum horário configurado para esta turma.
            </v-alert>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="section-card fill-height">
            <div class="section-card__title">
              <v-icon small class="mr-2" :color="theme.secondary">
                mdi-account-tie-outline
              </v-icon>
              Professor responsável
            </div>

            <div class="d-flex align-center flex-wrap">
              <v-avatar
                size="68"
                class="mr-4 mb-3 mb-sm-0"
                :color="teacherAvatarColor"
              >
                <v-img
                  v-if="teacherAvatar"
                  :src="teacherAvatar"
                  cover
                  class="class-overview__teacher-avatar"
                />
                <span v-else class="white--text font-weight-bold">
                  {{ teacherInitials }}
                </span>
              </v-avatar>

              <div>
                <div class="section-card__headline">
                  {{ teacherName }}
                </div>
                <div class="caption grey--text text--darken-1">
                  {{ teacherEmail }}
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <div class="section-block">
        <div class="section-header">
          <div class="section-header__title">
            {{ membersTitle || theme.membersTitle || "Participantes da turma" }}
          </div>
        </div>

        <slot name="members-content" :members="safeMembers">
          <v-row v-if="safeMembers.length" dense>
            <v-col
              v-for="(member, index) in safeMembers"
              :key="member.id || `${member.email}-${index}`"
              cols="12"
              sm="6"
            >
              <slot name="member" :member="member">
                <ClassMemberCard
                  :student="member"
                  :theme="theme"
                  :showBilling="showBilling"
                />
              </slot>
            </v-col>
          </v-row>

          <template v-else>
            <slot name="members-empty">
              <v-alert type="info" text class="mb-0">
                {{ membersEmptyText }}
              </v-alert>
            </slot>
          </template>
        </slot>
      </div>
    </v-card-text>

    <v-dialog v-if="allowLevelEdit" v-model="levelDialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Atualizar nível da turma</v-card-title>
        <v-card-text>
          <div class="caption grey--text text--darken-1 mb-4">
            Selecione o nível pedagógico atual da turma.
          </div>

          <v-select
            v-model="selectedLevel"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            label="Nível da turma"
            outlined
            dense
            hide-details="auto"
          />

          <div v-if="safeMembers.length" class="mt-4">
            <div class="caption grey--text text--darken-1 mb-2">
              Deseja atualizar o nível dos alunos também? Selecione quais:
            </div>
            <v-checkbox
              v-for="member in safeMembers"
              :key="member.id"
              v-model="cascadeStudentIds"
              :label="member.name"
              :value="member.id"
              dense
              hide-details
              class="mt-0"
            />
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="closeLevelDialog">Cancelar</v-btn>
          <v-btn
            dark
            rounded
            :color="theme.primary"
            :loading="levelBusy"
            :disabled="
              selectedLevel === null ||
              (+selectedLevel === +classData.level &&
                cascadeStudentIds.length === 0)
            "
            @click="submitLevelUpdate"
          >
            Salvar nível
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import ClassMemberCard from "@/components/classes/ClassMemberCard.vue";
import {
  ENGLISH_LEVELS,
  WEEK_DAY_ITEMS,
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupLevel,
  displayGroupStatus,
  groupStatusColor,
  initialsFromName,
} from "@/shared/classPresentation";

export default {
  name: "ClassOverviewCard",

  components: {
    ClassMemberCard,
  },

  props: {
    classData: {
      type: Object,
      required: true,
    },
    normalizedTimes: {
      type: Array,
      default: () => [],
    },
    members: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
    membersTitle: {
      type: String,
      default: "",
    },
    membersEmptyText: {
      type: String,
      default: "Nenhum participante disponível para exibição no momento.",
    },
    showBilling: {
      type: Boolean,
      default: false,
    },
    allowLevelEdit: {
      type: Boolean,
      default: false,
    },
    hideHero: {
      type: Boolean,
      default: false,
    },
    levelOptions: {
      type: Array,
      default: () => ENGLISH_LEVELS,
    },
    levelBusy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      levelDialog: false,
      selectedLevel: null,
      cascadeStudentIds: [],
    };
  },

  watch: {
    "classData.level": {
      immediate: true,
      handler(level) {
        this.selectedLevel =
          level === undefined || level === null ? null : +level;
      },
    },
  },

  computed: {
    safeMembers() {
      return Array.isArray(this.members) ? this.members : [];
    },
    themeStyle() {
      return {
        "--class-primary": this.theme.primary,
        "--class-secondary": this.theme.secondary,
        "--class-surface": this.theme.surface,
        "--class-surface-alt": this.theme.surfaceAlt || this.theme.surface,
        "--class-border": this.theme.border,
      };
    },
    heroStyle() {
      const gradient = `linear-gradient(135deg, ${this.theme.primary}CC, ${this.theme.secondary}CC)`;
      return {
        backgroundImage: `${gradient}, url(${this.theme.toolbarImage})`,
      };
    },
    teacherName() {
      return (
        this.classData?.teacher?.full_name ||
        this.classData?.teacher?.name ||
        "Professor(a) não informado(a)"
      );
    },
    teacherEmail() {
      return this.classData?.teacher?.email || "E-mail indisponível";
    },
    teacherAvatar() {
      return this.classData?.teacher?.avatar || "";
    },
    teacherInitials() {
      return initialsFromName(this.teacherName);
    },
    teacherAvatarColor() {
      return this.teacherAvatar ? "" : this.theme.secondary;
    },
  },

  methods: {
    displayLevel(level) {
      return displayGroupLevel(level);
    },
    displayFormat(format) {
      return displayGroupFormat(format);
    },
    displayFrequency(frequency) {
      return displayGroupFrequency(frequency);
    },
    displayStatus(status) {
      return displayGroupStatus(status);
    },
    statusColor(status) {
      return groupStatusColor(status);
    },
    weekDayLabel(weekDay) {
      return (
        WEEK_DAY_ITEMS.find((item) => item.value === +weekDay)?.text || "-"
      );
    },
    openLevelDialog() {
      this.selectedLevel =
        this.classData.level === undefined || this.classData.level === null
          ? null
          : +this.classData.level;
      this.cascadeStudentIds = [];
      this.levelDialog = true;
    },
    closeLevelDialog() {
      this.levelDialog = false;
      this.selectedLevel =
        this.classData.level === undefined || this.classData.level === null
          ? null
          : +this.classData.level;
    },
    submitLevelUpdate() {
      const levelUnchanged = +this.selectedLevel === +this.classData.level;
      if (
        this.selectedLevel === null ||
        (levelUnchanged && this.cascadeStudentIds.length === 0)
      ) {
        return;
      }

      this.$emit("update-level", {
        level: +this.selectedLevel,
        studentIds: [...this.cascadeStudentIds],
      });
      this.levelDialog = false;
    },
  },
};
</script>

<style scoped lang="css">
.class-overview {
  width: 100%;
  overflow: hidden;
  background: radial-gradient(
      circle at top right,
      rgba(255, 255, 255, 0.9),
      transparent 35%
    ),
    linear-gradient(180deg, var(--class-surface) 0%, #ffffff 100%);
  border: 1px solid var(--class-border);
  box-shadow: 0 25px 60px -45px rgba(15, 23, 42, 0.35);
}

.class-overview__hero {
  position: relative;
  min-height: 160px;
  background-position: center;
  background-size: cover;
}

.class-overview__hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.08),
    rgba(15, 23, 42, 0.28)
  );
}

.class-overview__hero-content {
  position: relative;
  z-index: 1;
  min-height: 160px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.class-overview__eyebrow {
  letter-spacing: 0.08em;
}

.info-box {
  height: 100%;
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid var(--class-border);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.info-box__label {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.info-box__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.info-box__value {
  margin-top: 6px;
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
}

.info-box__helper {
  margin-top: 8px;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.4;
}

.section-card {
  height: 100%;
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.94) 0%,
    var(--class-surface-alt) 100%
  );
  border: 1px solid var(--class-border);
}

.section-card__title {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
}

.section-card__headline {
  color: #111827;
  font-size: 1.05rem;
  font-weight: 700;
}

.class-overview__teacher-avatar {
  width: 100%;
  height: 100%;
}

.section-block {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.section-header__title {
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
}

@media (max-width: 959px) {
  .class-overview__hero-content {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
